import { useEffect, useState, useContext } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { CartContext } from "../context/CartContext";
import { API_BASE } from "../utils/api";
import { getMyAccountId } from "../utils/config";
import { useLanguage } from "../context/LanguageContext";
import { downloadFile } from "../utils/downloadFile";
import { useRidePreview } from "../hooks/useRidePreview";
import RidePreviewModal from "../components/Modals/RidePreviewModal";
import { FaFilePdf, FaFileCode, FaEye, FaBan } from "react-icons/fa";
import { duplicateOrder as duplicateOrderUtil } from "../utils/duplicateOrder";
import OrderStatusTracker from "../components/OrderStatusTracker";

export default function OrderDetailPage() {
    const { t, switchLanguage, language } = useLanguage();
    const { orderId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const { setCartItems } = useContext(CartContext);
    const MYACCOUNT_ID = getMyAccountId();
    const passedOrder = location.state?.order || null;

    const [header, setHeader] = useState(passedOrder);
    const [items, setItems] = useState([]);
    const [previewImg, setPreviewImg] = useState(null);
    const [duplicating, setDuplicating] = useState(false);

    const user = JSON.parse(localStorage.getItem("user"));
    const [showApproveConfirm, setShowApproveConfirm] = useState(false);
    const [showApproveProformaConfirm, setShowApproveProformaConfirm] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [showApproveSuccess, setShowApproveSuccess] = useState(false);
    const [showMoreId, setShowMoreId] = useState(null);

    // RESPETANDO tus espacios
    const isSupervisor = user?.groupId === "     9";

    // Pedido pendiente
    const isPendingApproval = header?.clase === "+";

    // Un vendedor/asesor (groupId 1) nunca ve el aviso/botón de anulación: solo aplica a clientes finales.
    // OJO: el Aprobador (groupId 9) también inicia sesión como empleado (usa user.idEmployee en approve_order),
    // así que "vendedor" NO puede detectarse con idEmployee — se distingue por groupId, igual que isAdvisor en el resto del código.
    const isVendedor = !user?.changeCustomer && user?.groupId === "     1";

    // Plazo de anulación (días) configurado por cliente en el ERP
    const CANCEL_WINDOW_DAYS = Number(user?.demanda) || 0;
    const CANCEL_WINDOW_HOURS = CANCEL_WINDOW_DAYS * 24;

    // Si la orden aun no fue aprobada (clase "+"), se anula sin limite de tiempo,
    // sin importar demanda. El plazo por demanda solo aplica una vez aprobada.
    const isWithinCancelWindow = (() => {
        if (isPendingApproval) return true;
        if (!header?.orderDate) return false;
        const deadline = new Date(header.orderDate);
        deadline.setDate(deadline.getDate() + CANCEL_WINDOW_DAYS);
        deadline.setHours(23, 59, 59, 999);
        return new Date() <= deadline;
    })();

    // Solo se puede anular si aún no fue facturada (crossId null), es un
    // pedido/orden pendiente/proforma (tipo PE), el supervisor puede hacerlo,
    // y no es un vendedor. Si la orden no esta aprobada, el boton se habilita
    // siempre (sin importar demanda); si ya esta aprobada, requiere demanda > 0
    // y el plazo vencido no oculta la opcion: la deja visible pero bloqueada
    // (isWithinCancelWindow).
    const isCancellable =
        isSupervisor &&
        header?.tipo === "PE" &&
        header?.crossId === null &&
        !isVendedor &&
        (isPendingApproval || CANCEL_WINDOW_DAYS > 0);

    // Solicitud de Proforma con statusId=3 (pendiente de aprobación)
    const isProformaAwaitingApproval =
        header?.tipo === "PE" &&
        header?.clase === " " &&
        header?.statusId === '3';

    // Solicitud de Proforma aprobada (statusId=4) — puede generar orden
    const isProformaApproved =
        header?.tipo === "PE" &&
        header?.clase === " " &&
        header?.statusId === '4';

    const {
        rideOpen,
        rideUrl,
        openRidePreview,
        closeRidePreview,
    } = useRidePreview();

    // ============================
    // 1) Cargar items del pedido
    // ============================
    useEffect(() => {
        if (!orderId) return;

        const fetchItems = async () => {
            try {
                const res = await fetch(
                    `${API_BASE}/orders-ultra/api/ws/listItems_ecommerce?order_id=${orderId}`
                );
                const data = await res.json();
                setItems(data);
            } catch (err) {
                console.error("Error cargando items:", err);
            }
        };

        fetchItems();
    }, [orderId]);

    const handleDuplicateOrder = async (order, items) => {
        try {
            setDuplicating(true);
            await duplicateOrderUtil(order, items, { history, setCartItems });
        } finally {
            setDuplicating(false);
        }
    };



    const approveProforma = async () => {
        try {
            const res = await fetch(
                `${API_BASE}/orders-ultra/api/ws/update_order_status?order_id=${header.orderId}&status_id=4`,
                { method: "POST" }
            );

            const text = await res.text();
            const data = JSON.parse(text);

            if (!res.ok || data.status !== "OK") {
                throw new Error("Error aprobando proforma");
            }

            history.push("/dashboard", { activeTab: "proformas" });

        } catch (err) {
            alert(err.message);
        }
    };

    const approveOrder = async () => {
        try {
            const res = await fetch(
                `${API_BASE}/orders-ultra/api/ws/approve_order?order_id=${header.orderId}&id_employee=${user.idEmployee}`,
                { method: "POST" }
            );

            const text = await res.text();
            const data = JSON.parse(text);

            if (!res.ok || data.status !== "OK") {
                throw new Error("Error approving order");
            }

            // El pedido aprobado ya cuenta como consumo real de presupuesto — avisar a
            // los indicadores de presupuesto (navbar) para que se actualicen de una vez.
            window.dispatchEvent(new Event("budget-updated"));
            // Ya no cuenta como pendiente — refrescar el badge del menu de usuario.
            window.dispatchEvent(new Event("pending-approval-updated"));

            if (CANCEL_WINDOW_DAYS > 0 && !isVendedor) {
                setShowApproveSuccess(true);
            } else {
                history.push("/orders-history", { activeTab: "pending_approval" });
            }

        } catch (err) {
            alert(err.message);
        }
    };

    const cancelOrder = async () => {
        try {
            setCancelling(true);
            const res = await fetch(
                `${API_BASE}/orders-ultra/api/ws/anular_order?order_id=${header.orderId}&user=${encodeURIComponent(user.userId)}&motivo=${encodeURIComponent(cancelReason.trim())}`,
                { method: "POST" }
            );

            const text = await res.text();

            if (!res.ok) {
                throw new Error(text || "Error anulando orden");
            }

            const data = JSON.parse(text);
            if (data.status !== "OK") {
                throw new Error("Error anulando orden");
            }

            // La orden anulada deja de contar como consumo real de presupuesto —
            // refrescar el indicador (igual que al aprobar, ver approveOrder).
            window.dispatchEvent(new Event("budget-updated"));

            history.push("/orders-history");

        } catch (err) {
            alert(err.message);
        } finally {
            setCancelling(false);
        }
    };



    const DOC_TYPE_STYLES = {
        "PE-*":  { bg: "#80FF80", accent: "#16a34a", label: "Pedido" },
        "PE-+":  { bg: "#C8FFAD", accent: "#65a30d", label: "Orden de Compra Pendiente" },
        "FE- ":  { bg: "#FFC6AA", accent: "#ea580c", label: "Guía" },
        "FJ- ":  { bg: "#FFC6AA", accent: "#ea580c", label: "Guía" },
        "FC- ":  { bg: "#C4ECFF", accent: "#2563eb", label: "Factura" },
        "FC--":  { bg: "#ffffff", accent: "#9ca3af", label: "Nota Crédito" },
        "PE- ":  { bg: "#FFFFB3", accent: "#9ca3af", label: "Solicitud de Proforma" },
    };

    const getDocStyle = () => {
        const { tipo, clase } = header || {};
        const key = `${tipo}-${clase}`;
        return DOC_TYPE_STYLES[key] || { bg: "#ffffff", accent: "#e5e7eb", label: tipo || "" };
    };

    if (!header) {
        return (
            <div className="text-center mt-40 text-gray-600 text-xl">
                Cargando order #{orderId}...
            </div>
        );
    }

    const docStyle = getDocStyle();

    return (
        <div className="container mx-auto px-4 py-10 mt-24">

            {/* 🔴 SIN ALTURA FORZADA */}
            <div className="flex flex-col">

                {/* ============================
                   ENCABEZADO
                ============================ */}
                <div
                    className="sticky top-0 z-20 shadow rounded-xl p-6 mb-4 border border-gray-200 border-l-4"
                    style={{
                        background: docStyle.bg !== "#ffffff"
                            ? `linear-gradient(to right, ${docStyle.bg} 5%, #ffffff 100%)`
                            : "#ffffff",
                        borderLeftColor: docStyle.accent
                    }}
                >
                    <div className="flex justify-between items-center flex-wrap gap-4">

                        {/* IZQUIERDA */}
                        <button
                            onClick={() => history.goBack()}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-medium shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-all"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                            {t.btnBack}
                        </button>

                        {/* DERECHA */}
                        <div className="flex items-center gap-3 text-right">
                            <span
                                className="hidden text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wide"
                                style={{ backgroundColor: docStyle.accent, color: "#ffffff" }}
                            >
                                {docStyle.label}
                            </span>
                            <h1 className="text-2xl font-bold text-gray-900">
                                {header.tipo === "FC" && header.clase === " " ? t.invoinceTitle + ' ' + header.localId + ' - ' + header.issueId + ' - ' :
                                    header.tipo === "FC" && header.clase === "-" ? t.creditNoteTitle + ' ' + header.localId + ' - ' + header.issueId + ' - ' :
                                        header.tipo === "FE" || header.tipo === "FJ" ? t.guideTitle + ' ' + header.localId + ' - ' + header.issueId + ' - ' :
                                            header.tipo === "PE" && header.clase === " " && header.statusId === " " ? t.proformaTitle :
                                                header.tipo === "PE" && header.clase === " " ? "Proforma" :
                                                        header.clase === "+" ? "Orden de Compra Pendiente " :
                                                    t.pedidoTitle} {header.orderNumber}
                            </h1>
                        </div>

                    </div>

                    <div className="mt-6 flex items-center justify-between gap-6 text-sm">

                        {/* IZQUIERDA - INFO */}
                        <div className="flex items-center gap-12 w-1/2">

                            <div>

                                <p><strong>Fecha:</strong>  {new Date(header.orderDate).toLocaleDateString("es-ES", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric"
                                })}</p>
                            </div>

                            <div>
                                {header.invoiceNumber && (
                                    <p>
                                        <strong>Orden de compra:</strong> {header.invoiceNumber}
                                    </p>
                                )}
                            </div>

                            {header.tipo === "PE" && header.clase === " " && (
                                <>
                                    {header.carNumber > 0 && (
                                        <div>
                                            <p><strong>Tiempo de entrega:</strong> {header.carNumber}</p>
                                        </div>
                                    )}
                                    {header.noComps > 1&& (
                                        <div>
                                            <p><strong>Válidez oferta:</strong> {header.noComps}</p>
                                        </div>
                                    )}
                                </>
                            )}

                        </div>

                        {/* DERECHA - ACCIONES */}
                        <div className="flex items-center gap-3">

                            {header.tipo === "PE" && header.clase === "*" && !isPendingApproval && (
                                <button
                                    onClick={() => {
                                        localStorage.removeItem("proforma_source");
                                        handleDuplicateOrder(header, items);
                                    }}
                                    disabled={duplicating}
                                    className={`px-4 py-2 rounded text-sm font-semibold transition
                                         ${duplicating
                                            ? "bg-gray-400 cursor-not-allowed text-white"
                                            : "bg-brand-primary text-white hover:brightness-95"
                                        }`}
                                >
                                    ↻ {t.btnOrderAgain}
                                </button>
                            )}

                            {isSupervisor && isPendingApproval && (
                                <button
                                    onClick={() => setShowApproveConfirm(true)}
                                    className="px-4 py-2 bg-green-600 text-white rounded
                               hover:bg-green-700 transition font-semibold text-sm"
                                >
                                    ✔ {t.btnApprovarOrder}
                                </button>
                            )}

                            {isProformaApproved && (
                                <button
                                    onClick={() => {
                                        localStorage.setItem("order_mode", "order");
                                        window.dispatchEvent(new Event("orderModeChange"));
                                        localStorage.setItem("proforma_source", JSON.stringify({
                                            pedidoNumber: header.orderNumber,
                                            crossId: header.orderId,
                                            carNumber: header.carNumber,
                                            noComps: header.noComps
                                        }));
                                        handleDuplicateOrder(header, items);
                                    }}
                                    disabled={duplicating}
                                    className={`px-4 py-2 rounded text-sm font-semibold transition
                                         ${duplicating
                                            ? "bg-gray-400 cursor-not-allowed text-white"
                                            : "bg-green-600 text-white hover:bg-green-700"
                                        }`}
                                >
                                 {t.btnGenerarOrden}
                                </button>
                            )}

                            {isProformaAwaitingApproval && (
                                <button
                                    onClick={() => setShowApproveProformaConfirm(true)}
                                    className="px-4 py-2 bg-green-600 text-white rounded
                                       hover:bg-green-700 transition font-semibold text-sm"
                                >
                                Aprobar esta Proforma de Serec
                                </button>
                            )}

                            {/* OPCIONES */}
                            <div
                                className="relative flex justify-center"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* BOTÓN ⋮ */}
                                <button
                                    onClick={() =>
                                        setShowMoreId(showMoreId === header.orderId ? null : header.orderId)
                                    }
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-medium shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-all"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <circle cx="12" cy="5" r="1.5" />
                                        <circle cx="12" cy="12" r="1.5" />
                                        <circle cx="12" cy="19" r="1.5" />
                                    </svg>
                                    {t.btnMoreOptions}
                                </button>

                                {/* MENÚ DESPLEGABLE */}
                                {showMoreId === header.orderId && (
                                    <div className="absolute right-0 top-full mt-2 w-44 bg-white border rounded-md shadow-lg z-50">

                                        {/* VER RIDE */}
                                        <button
                                            onClick={() => {
                                                openRidePreview(
                                                    `${API_BASE}/orders-ultra/api/ws/generate_ride_order?order_id=${header.orderId}`
                                                );
                                                setShowMoreId(null);
                                            }}
                                            className="
                                                    w-full
                                                    flex items-center gap-2
                                                    px-4 py-2
                                                    text-sm
                                                    hover:bg-gray-100
                                                "
                                        >
                                            <FaEye className="text-xs text-gray-600" />
                                            <span>Ver RIDE</span>
                                        </button>

                                        {/* DESCARGAR FE PHOTO */}
                                        {(header.tipo === "FE" || header.tipo === "FJ") && (

                                            <button
                                                onClick={() => {
                                                    openRidePreview(
                                                        `${API_BASE}/orders-ultra/api/ws/generate_ride_order?order_id=${header.orderId}&report_13=true`
                                                    );
                                                    setShowMoreId(null);
                                                }}
                                                className="
                                                w-full
                                                flex items-center gap-2
                                                px-4 py-2
                                                text-sm
                                                hover:bg-gray-100
                                            "
                                            >
                                                <FaEye className="text-xs text-gray-600" />
                                                <span>Ver RIDE imágenes</span>
                                            </button>
                                        )}

                                        {/* DESCARGAR PDF */}
                                        <button
                                            onClick={() => {
                                                downloadFile(
                                                    `${API_BASE}/orders-ultra/api/ws/generate_ride_order?order_id=${header.orderId}`,
                                                    header.tipo === "PE"
                                                        ? `${t.pedidoTitle} ${header.orderNumber}.pdf`
                                                        : `${header.autorizacion}.pdf`,
                                                    header.orderId
                                                );
                                                setShowMoreId(null);
                                            }}
                                            className="    w-full
                                                flex items-center gap-2
                                                px-4 py-2
                                                text-sm
                                                hover:bg-gray-100"
                                        >
                                            <FaFilePdf className="text-xs text-blue-600" />
                                            Descargar PDF
                                        </button>
                                        {/* DESCARGAR XML */}
                                        {header.tipo !== "PE" && (

                                            <button
                                                onClick={() => {
                                                    downloadFile(
                                                        `${API_BASE}/orders-ultra/api/ws/download_xml?claveAcceso=${header.autorizacion}`,
                                                        `${header.autorizacion}.xml`,
                                                        `xml-${header.orderId}`
                                                    );
                                                    setShowMoreId(null);
                                                }}
                                                className="
                                                w-full
                                                flex items-center gap-2
                                                px-4 py-2
                                                text-sm
                                                hover:bg-gray-100
                                            "
                                            >
                                                <FaFileCode className="text-xs text-blue-600" />
                                                <span>Descargar XML</span>
                                            </button>
                                        )}

                                        {/* ANULAR ORDEN */}
                                        {isCancellable && (
                                            <button
                                                onClick={() => {
                                                    if (!isWithinCancelWindow) return;
                                                    setShowCancelConfirm(true);
                                                    setShowMoreId(null);
                                                }}
                                                disabled={!isWithinCancelWindow}
                                                title={!isWithinCancelWindow ? t.cancelOrderExpired : undefined}
                                                className={`
                                                w-full
                                                flex items-center gap-2
                                                px-4 py-2
                                                text-sm
                                                ${isWithinCancelWindow
                                                        ? "text-red-600 hover:bg-red-50"
                                                        : "text-gray-400 cursor-not-allowed"
                                                    }
                                            `}
                                            >
                                                <FaBan className={`text-xs ${isWithinCancelWindow ? "text-red-600" : "text-gray-400"}`} />
                                                <span>{t.btnCancelOrder}</span>
                                            </button>
                                        )}

                                    </div>
                                )}
                            </div>

                        </div>

                    </div>

                    {header.notes && (
                        <div className="flex justify-between items-center flex-wrap gap-4 text-sm mt-6">
                            <p><strong>Notas:</strong> {header.notes}</p>
                        </div>
                    )}

                    {header.tipo === "PE" && header.clase !== " " && (
                        <OrderStatusTracker
                            orderId={header.orderId}
                            accentColor={docStyle.accent}
                            onOpenRide={openRidePreview}
                        />
                    )}

                </div>

                {/* ============================
                   TABLA (SIN SCROLL)
                ============================ */}
                <div className="bg-white shadow rounded-lg border border-gray-200 p-2 sm:p-0">

                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 border-b">
                            <tr className="text-left text-gray-700 font-semibold">
                                <th className="p-2"></th>
                                <th className="p-2">Código</th>
                                <th className="p-2">Descripción</th>
                                <th className="text-right p-2">Cantidad</th>
                                <th className="text-right p-2">Precio</th>
                                <th className="text-right p-2">IVA</th>
                                <th className="text-right p-2">Total</th>
                            </tr>
                        </thead>

                        <tbody>
                            {items.map((i) => (
                                <tr key={i.orderLineId} className="border-b hover:bg-gray-50">
                                    <td className="p-2">
                                        <img
                                            src={`https://www.ultra-erp.pro/products/${MYACCOUNT_ID}/${i.productId}.jpg`}
                                            className="w-16 h-16 border rounded-md object-cover shadow-sm transition duration-300 align-middle cursor-pointer"
                                            onClick={() =>
                                                setPreviewImg(`https://www.ultra-erp.pro/products/${MYACCOUNT_ID}/${i.productId}.jpg`)
                                            }
                                        />
                                    </td>
                                    <td className="p-2">{i.productId}</td>
                                    <td className="p-2">
                                        <span className="px-1 rounded" style={i.notes && i.notes !== i.productName ? { backgroundColor: '#00D2D2' } : {}}>
                                            {i.notes || i.productName}
                                        </span>
                                    </td>
                                    <td className="text-right p-2">{i.quantity}</td>
                                    <td className="text-right p-2">{i.unitPrice?.toFixed(4)}</td>
                                    <td className="text-right p-2">{i.taxValue > 0 ? "*" : ""}</td>
                                    <td className="text-right p-2 font-semibold text-brand-primary">
                                        {i.totalPrice?.toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* ============================
                   TOTALES (PEGADOS)
                ============================ */}
                <div className="
                    bg-white shadow-lg rounded-xl p-4 mt-4 border border-gray-300
                    w-full md:max-w-md md:ml-auto
                ">
                    <div className="flex justify-between mb-2">
                        <span>Subtotal:</span>
                        <span className="font-semibold">{header.taxable?.toFixed(2)}</span>
                    </div>

                    {header.totalExento > 0 && (
                        <div className="flex justify-between mb-2">
                            <span>Subtotal 0%:</span>
                            <span className="font-semibold">{header.totalExento?.toFixed(2)}</span>
                        </div>
                    )}

                    <div className="flex justify-between mb-2">
                        <span>IVA:</span>
                        <span className="font-semibold">{header.taxTotal?.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between text-lg font-bold mt-4 border-t pt-3">
                        <span>Total:</span>
                        <span className="text-brand-primary">${header.total?.toFixed(2)}</span>
                    </div>
                </div>

            </div>

            {previewImg && (
                <div
                    className="fixed inset-0 bg-black/60 flex items-center justify-center z-[999]"
                    onClick={() => setPreviewImg(null)}
                >
                    <img
                        src={previewImg}
                        className="max-w-[90%] max-h-[90%] rounded-lg"
                    />
                </div>
            )}
            {showApproveConfirm && (
                <div className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center">
                    <div className="mt-24 bg-black text-white px-10 py-6 rounded-xl shadow-2xl min-w-[420px] text-center">

                        <p className="mb-6 text-sm leading-relaxed">
                            {t.aproveeTitle}
                            <br />
                            <span className="text-gray-400">
                                {t.aproveeText}
                            </span>
                        </p>

                        <div className="flex justify-center gap-6">
                            {/* YES */}
                            <button
                                onClick={async () => {
                                    setShowApproveConfirm(false);
                                    await approveOrder();
                                }}
                                className="flex items-center gap-2 px-6 py-2
                               border border-green-500 rounded-md
                               text-green-400 hover:bg-green-500/10 transition"
                            >
                                ✓ {t.yesAprovee}
                            </button>

                            {/* NO */}
                            <button
                                onClick={() => setShowApproveConfirm(false)}
                                className="flex items-center gap-2 px-6 py-2
                               border border-red-500 rounded-md
                               text-red-400 hover:bg-red-500/10 transition"
                            >
                                ✕  {t.cancel}
                            </button>
                        </div>


                    </div>
                </div>
            )}
            {showApproveSuccess && (
                <div className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center">
                    <div className="mt-24 bg-black text-white px-10 py-6 rounded-xl shadow-2xl min-w-[420px] text-center">

                        <p className="mb-6 text-sm leading-relaxed">
                            {t.approveSuccessTitle}
                            <br />
                            <span className="text-gray-400">
                                {t.approveSuccessTimePrefix} {CANCEL_WINDOW_HOURS}{" "}
                                {CANCEL_WINDOW_HOURS === 1 ? t.hourLabelSingular : t.hourLabelPlural}{" "}
                                {t.approveSuccessTimeSuffix}
                            </span>
                        </p>

                        <div className="flex justify-center">
                            <button
                                onClick={() => {
                                    setShowApproveSuccess(false);
                                    history.push("/orders-history", { activeTab: "pending_approval" });
                                }}
                                className="flex items-center gap-2 px-6 py-2
                               border border-green-500 rounded-md
                               text-green-400 hover:bg-green-500/10 transition"
                            >
                                ✓ {t.understood}
                            </button>
                        </div>

                    </div>
                </div>
            )}
            {showApproveProformaConfirm && (
                <div className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center">
                    <div className="mt-24 bg-black text-white px-10 py-6 rounded-xl shadow-2xl min-w-[420px] text-center">

                        <p className="mb-6 text-sm leading-relaxed">
                            {t.approveProformaTitle}
                            <br />
                            <span className="text-gray-400">
                                {t.approveProformaText}
                            </span>
                        </p>

                        <div className="flex justify-center gap-6">
                            <button
                                onClick={async () => {
                                    setShowApproveProformaConfirm(false);
                                    await approveProforma();
                                }}
                                className="flex items-center gap-2 px-6 py-2
                                   border border-green-500 rounded-md
                                   text-green-400 hover:bg-green-500/10 transition"
                            >
                                ✓ {t.yesAprovee}
                            </button>

                            <button
                                onClick={() => setShowApproveProformaConfirm(false)}
                                className="flex items-center gap-2 px-6 py-2
                                   border border-red-500 rounded-md
                                   text-red-400 hover:bg-red-500/10 transition"
                            >
                                ✕ {t.cancel}
                            </button>
                        </div>

                    </div>
                </div>
            )}
            {showCancelConfirm && (
                <div className="fixed inset-0 z-[100] bg-black/70 flex items-start justify-center">
                    <div className="mt-24 bg-black text-white px-10 py-6 rounded-xl shadow-2xl min-w-[420px] text-center">

                        <p className="mb-4 text-sm leading-relaxed">
                            {t.cancelOrderTitle}
                            <br />
                            <span className="text-gray-400">
                                {t.cancelOrderText}
                            </span>
                        </p>

                        {/* Orden aun no aprobada: se elimina por completo, no aplica motivo */}
                        {!isPendingApproval && (
                            <textarea
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder={t.cancelOrderReasonPlaceholder}
                                rows={3}
                                disabled={cancelling}
                                className="w-full mb-6 px-3 py-2 rounded-md
                                   bg-gray-900 border border-gray-600
                                   text-sm text-white placeholder-gray-500
                                   focus:outline-none focus:border-red-500
                                   disabled:opacity-50"
                            />
                        )}

                        <div className="flex justify-center gap-6">
                            {/* YES */}
                            <button
                                onClick={async () => {
                                    setShowCancelConfirm(false);
                                    await cancelOrder();
                                }}
                                disabled={cancelling || (!isPendingApproval && !cancelReason.trim())}
                                className="flex items-center gap-2 px-6 py-2
                               border border-red-500 rounded-md
                               text-red-400 hover:bg-red-500/10 transition
                               disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                ✓ {t.yesCancelOrder}
                            </button>

                            {/* NO */}
                            <button
                                onClick={() => {
                                    setShowCancelConfirm(false);
                                    setCancelReason("");
                                }}
                                disabled={cancelling}
                                className="flex items-center gap-2 px-6 py-2
                               border border-gray-500 rounded-md
                               text-gray-300 hover:bg-gray-500/10 transition"
                            >
                                ✕ {t.cancel}
                            </button>
                        </div>


                    </div>
                </div>
            )}
            <RidePreviewModal
                open={rideOpen}
                url={rideUrl}
                onClose={closeRidePreview}
            />
        </div>

    );
}
